import { type RefObject, useCallback, useEffect, useRef } from 'react';

import type { CommunitySendableType } from '~/interfaces/community/community';
import { observeImpression, unobserveImpression } from '~/utils/nectarImpressionObserver';
import type { NectarPageBlock } from '~/utils/nectarPageBlock';

import { useNectarAnalyticsContext } from './useNectarAnalyticsContext';

export interface NectarImpressionDimensions {
  sendableId?: string;
  sendableType?: CommunitySendableType;
  // Impression surface. May be undefined for a card rendered on an untracked
  // surface (feed vs search/member profile); pair with `enabled: !!pageBlock`.
  pageBlock?: NectarPageBlock;
  communitySpaceId?: string;
}

interface UseNectarImpressionOptions {
  // false (default): fire on mount. true: fire once the element dwells in view.
  fireWhenInViewport?: boolean;
  // Whether to emit at all (default true). Pass `enabled: !!pageBlock` to opt a
  // card out of tracking on surfaces that don't supply a page block, or fold in
  // gate suppression (e.g. `enabled: !!pageBlock && !suppressed`).
  enabled?: boolean;
}

// Entity+surface combinations already counted this browsing session, keyed
// `${pageBlock}:${sendableType}:${sendableId}`. Module-level so it survives
// component unmount/remount — loading-state churn or list virtualization can't
// re-fire the same post and inflate totals. Resets on a full page reload (a new
// session); client emits once per post per surface per session, with cross-user
// uniqueness still resolved downstream by visit_token / anonymous_user_id.
const firedThisSession = new Set<string>();

const sessionKey = ({ pageBlock, sendableType, sendableId }: NectarImpressionDimensions): string =>
  `${pageBlock}:${sendableType}:${sendableId}`;

// Test-only: clear the per-session dedupe between cases.
export const __resetImpressionSessionForTests = (): void => {
  firedThisSession.clear();
};

/**
 * Emits a Nectar `impression` for a surfaced entity, at most once per post per
 * surface per browsing session (deduped across remounts via `firedThisSession`).
 *
 * Returns a ref to attach to the element to observe when `fireWhenInViewport`;
 * in the default (immediate) mode the ref is unused and the event fires on mount.
 */
export const useNectarImpression = (
  attrs: NectarImpressionDimensions,
  { fireWhenInViewport = false, enabled = true }: UseNectarImpressionOptions = {},
): RefObject<HTMLElement | null> => {
  const { trackImpression } = useNectarAnalyticsContext();
  const ref = useRef<HTMLElement>(null);
  const firedRef = useRef(false);

  // Latest attrs, so the fire closure/effects stay stable across renders.
  const attrsRef = useRef(attrs);
  attrsRef.current = attrs;

  const fire = useCallback((): void => {
    if (firedRef.current) return;
    const { pageBlock } = attrsRef.current;
    if (!pageBlock) return;
    firedRef.current = true;

    // Already counted this post+surface this session (e.g. the card remounted) —
    // don't re-fire.
    const key = sessionKey(attrsRef.current);
    if (firedThisSession.has(key)) return;
    firedThisSession.add(key);

    trackImpression({ ...attrsRef.current, pageBlock });
  }, [trackImpression]);

  // Immediate (default): fire on mount.
  useEffect(() => {
    if (fireWhenInViewport || !enabled) return;
    fire();
  }, [fireWhenInViewport, enabled, fire]);

  // Viewport: fire once the element dwells in view.
  useEffect(() => {
    const element = ref.current;
    if (!fireWhenInViewport || !enabled || !element) return;

    observeImpression(element, fire);
    return () => unobserveImpression(element);
  }, [fireWhenInViewport, enabled, fire]);

  return ref;
};
